import { create } from 'zustand'
import { Node, Edge, OnNodesChange, OnEdgesChange, OnConnect, applyNodeChanges, applyEdgeChanges, addEdge, Connection, MarkerType } from '@xyflow/react'
import {
  FlowVersionDto,
  FlowNodeDto,
  FlowEdgeDto,
  WorkflowNodeType,
  WorkflowConditionType,
  getNodeTypeColor,
  getNodeTypeLabel,
  NumpadConfig,
  NumpadInputStyle,
  CodexConfig,
  QrCodeConfig,
  QuestionAnswerConfig,
  QuestionAnswerType,
  ModeratorApprovalConfig,
  PoiLocationConfig,
  TextClueConfig,
  ClueGateConfig,
  AudioConfig,
  VideoConfig,
  ImageConfig,
  FormattedMessageConfig,
  UnlockAdventureConfig,
  GroupMembershipConfig,
  GiftRewardConfig,
  RouteConfig,
  DEFAULT_ROUTE_CONFIG,
  RouteNodeConfig,
  DEFAULT_ROUTE_NODE_CONFIG,
  DelayConfig,
  DelayTimeUnit,
  FlowVersionStatus,
} from '@features/adventures/types/workflow.types'
import {
  WorkflowValidationResult,
  NodeValidationState,
} from '@features/adventures/types/validation.types'
import { validateWorkflow as runValidation } from '@features/adventures/utils/validation'

// Custom node data type for React Flow
export interface WorkflowNodeData {
  label: string
  nodeType: WorkflowNodeType
  sequenceNumber?: number  // Human-readable sequential ID per node type (L1, L2, C1, etc.)
  description?: string
  rewardType?: string
  locationItemId?: string
  locationItem?: {
    id: string
    name: string
    quickDescription?: string
  }
  loopCooldownSeconds?: number
  loopTargetNodeId?: string
  maxLoops?: number
  timeBonusSeconds?: number
  isRequired: boolean
  numpadConfig?: NumpadConfig
  qrCodeConfig?: QrCodeConfig
  questionAnswerConfig?: QuestionAnswerConfig
  moderatorApprovalConfig?: ModeratorApprovalConfig
  poiLocationConfig?: PoiLocationConfig
  textClueConfig?: TextClueConfig
  clueGateConfig?: ClueGateConfig
  audioConfig?: AudioConfig
  videoConfig?: VideoConfig
  imageConfig?: ImageConfig
  formattedMessageConfig?: FormattedMessageConfig
  unlockAdventureConfig?: UnlockAdventureConfig
  groupMembershipConfig?: GroupMembershipConfig
  giftRewardConfig?: GiftRewardConfig
  codexConfig?: CodexConfig
  delayConfig?: DelayConfig
  routeNodeConfig?: RouteNodeConfig
  pausesTimer?: boolean
  config?: Record<string, unknown>
  conditions: Array<{
    id: string
    conditionType: number
    config: Record<string, unknown>
    branchKey?: string
    order: number
    logicGroupId?: string
  }>
  // Index signature for React Flow compatibility
  [key: string]: unknown
}

// Custom edge data type for React Flow
export interface WorkflowEdgeData {
  conditionBranchKey?: string
  isRouteEdge?: boolean
  routeConfig?: RouteConfig
  waypoints?: Array<{ x: number; y: number }>
  [key: string]: unknown
}

export type WorkflowNode = Node<WorkflowNodeData>
export type WorkflowEdge = Edge

interface WorkflowState {
  // Core state
  adventureId: string | null
  workflowId: string | null
  name: string
  description: string
  version: number
  isActive: boolean

  // Flow version state (new architecture)
  versionNumber: number | null
  versionStatus: FlowVersionStatus | null
  isCompiled: boolean

  // Collaboration state - tracks local change sequence for sync
  changeSequence: number

  // React Flow state
  nodes: WorkflowNode[]
  edges: WorkflowEdge[]

  // UI state
  selectedNodeId: string | null
  selectedEdgeId: string | null
  pendingEdgeSplit: { edgeId: string; x: number; y: number } | null
  isDirty: boolean
  isLoadingWorkflow: boolean  // Flag to prevent isDirty during load
  isLoading: boolean
  isSaving: boolean
  error: string | null
  validationResult: WorkflowValidationResult | null

  // Undo/Redo state
  undoStack: Array<{ nodes: WorkflowNode[]; edges: WorkflowEdge[] }>
  redoStack: Array<{ nodes: WorkflowNode[]; edges: WorkflowEdge[] }>
  canUndo: boolean
  canRedo: boolean

  // Actions - State management
  setAdventureId: (id: string | null) => void
  setLoading: (loading: boolean) => void
  setSaving: (saving: boolean) => void
  setError: (error: string | null) => void
  setDirty: (dirty: boolean) => void
  setValidationResult: (result: WorkflowValidationResult | null) => void
  setVersionInfo: (info: { versionNumber: number; status: FlowVersionStatus; isCompiled: boolean }) => void
  incrementChangeSequence: () => number
  getChangeSequence: () => number

  // Actions - Validation
  runValidation: () => WorkflowValidationResult
  clearValidation: () => void
  getNodeValidationState: (nodeId: string) => NodeValidationState | undefined

  // Actions - Undo/Redo
  undo: () => void
  redo: () => void
  pushToUndoStack: () => void

  // Actions - Load/Save
  loadWorkflow: (workflow: FlowVersionDto) => void
  initializeEmptyWorkflow: () => void
  resetWorkflow: () => void

  // Actions - React Flow callbacks
  onNodesChange: OnNodesChange<WorkflowNode>
  onEdgesChange: OnEdgesChange<WorkflowEdge>
  onConnect: OnConnect

  // Actions - Selection
  selectNode: (nodeId: string | null) => void
  selectEdge: (edgeId: string | null) => void
  setPendingEdgeSplit: (value: { edgeId: string; x: number; y: number } | null) => void

  // Actions - Node operations
  addNode: (type: WorkflowNodeType, position: { x: number; y: number }, label?: string, conditionType?: number, rewardType?: string) => string
  updateNode: (nodeId: string, data: Partial<WorkflowNodeData>) => void
  deleteNode: (nodeId: string) => void
  updateNodePosition: (nodeId: string, position: { x: number; y: number }) => void

  // Actions - Edge operations
  addEdge: (sourceId: string, targetId: string, label?: string, branchKey?: string) => string
  updateEdge: (edgeId: string, data: Partial<{ label: string; conditionBranchKey: string }>) => void
  deleteEdge: (edgeId: string) => void
  updateRouteConfig: (edgeId: string, config: Partial<RouteConfig>) => void
  updateEdgeWaypoints: (edgeId: string, waypoints: Array<{ x: number; y: number }>, options?: { pushUndo?: boolean; markDirty?: boolean }) => void

  // Actions - ID updates (for syncing with server-generated IDs)
  updateNodeId: (oldId: string, newId: string) => void
  updateEdgeId: (oldId: string, newId: string) => void

  // Actions - Restore operations (for rollback)
  addNodeFromData: (node: WorkflowNode) => void
  addEdgeFromData: (edge: WorkflowEdge) => void

  // Actions - Remote collaboration
  loadFromRemoteReplace: (
    nodes: Array<{ id: string; type: string; position: { x: number; y: number }; data: Record<string, unknown> }>,
    edges: Array<{ id: string; source: string; target: string; sourceHandle?: string; targetHandle?: string; data?: Record<string, unknown> }>,
    changeSequence?: number
  ) => void

  // Actions - Import/Export
  loadFromJson: (data: {
    name?: string
    description?: string
    nodes: Array<{
      id?: string
      nodeType: WorkflowNodeType
      label: string
      description?: string
      rewardType?: string
      positionX: number
      positionY: number
      locationItemId?: string
      loopCooldownSeconds?: number
      loopTargetNodeId?: string
      maxLoops?: number
      timeBonusSeconds?: number
      numpadConfig?: NumpadConfig
      qrCodeConfig?: QrCodeConfig
      codexConfig?: CodexConfig
      questionAnswerConfig?: QuestionAnswerConfig
      poiLocationConfig?: PoiLocationConfig
      textClueConfig?: TextClueConfig
      clueGateConfig?: ClueGateConfig
      audioConfig?: AudioConfig
      videoConfig?: VideoConfig
      imageConfig?: ImageConfig
      formattedMessageConfig?: FormattedMessageConfig
      unlockAdventureConfig?: UnlockAdventureConfig
      groupMembershipConfig?: GroupMembershipConfig
      giftRewardConfig?: GiftRewardConfig
      exitMessage?: string
      order: number
      isRequired: boolean
      config?: Record<string, unknown>
      conditions?: Array<{
        id?: string
        conditionType: number
        config: Record<string, unknown>
        branchKey?: string
        order: number
        logicGroupId?: string
      }>
    }>
    edges: Array<{
      id?: string
      sourceNodeId: string
      targetNodeId: string
      sourceHandle?: string
      targetHandle?: string
      label?: string
      conditionBranchKey?: string
      isRouteEdge?: boolean
      routeConfig?: RouteConfig
      order: number
    }>
  }) => void

  // Actions - Helpers
  getSerializableState: () => {
    flowData: {
      nodes: FlowNodeDto[]
      edges: FlowEdgeDto[]
    }
    description?: string
  }
}

// Parse the config field - it may be a JSON string or already an object
function parseConfigField(config: unknown): Record<string, unknown> | undefined {
  if (!config) return undefined
  if (typeof config === 'string') {
    try {
      return JSON.parse(config) as Record<string, unknown>
    } catch {
      console.warn('Failed to parse config JSON string:', config)
      return undefined
    }
  }
  if (typeof config === 'object') {
    return config as Record<string, unknown>
  }
  return undefined
}

// Convert backend DTO to React Flow node
function dtoToFlowNode(node: FlowNodeDto): WorkflowNode {
  // Extract conditions from node
  const conditions = node.conditions || []

  // BACKWARD COMPATIBILITY: Convert old Decision nodes with challenge conditions to new node types
  let effectiveNodeType = node.nodeType
  if (node.nodeType === WorkflowNodeType.Decision) {
    // Check for challenge condition types
    const challengeCondition = conditions.find(c =>
      [
        WorkflowConditionType.NumericInput,
        WorkflowConditionType.QuestionAnswer,
        WorkflowConditionType.QRCode,
        WorkflowConditionType.ModeratorApproval,
      ].includes(c.conditionType)
    )

    // Check for challenge configs
    const hasNumpadConfig = node.numpadConfig
    const hasQrCodeConfig = node.qrCodeConfig
    const hasQuestionAnswerConfig = node.questionAnswerConfig
    const hasModeratorApprovalConfig = node.moderatorApprovalConfig

    // Map to new node type based on condition or config
    if (hasNumpadConfig || challengeCondition?.conditionType === WorkflowConditionType.NumericInput) {
      effectiveNodeType = WorkflowNodeType.NumericInput
    } else if (hasQrCodeConfig || challengeCondition?.conditionType === WorkflowConditionType.QRCode) {
      effectiveNodeType = WorkflowNodeType.QRCode
    } else if (hasQuestionAnswerConfig || challengeCondition?.conditionType === WorkflowConditionType.QuestionAnswer) {
      effectiveNodeType = WorkflowNodeType.QuestionAnswer
    } else if (hasModeratorApprovalConfig || challengeCondition?.conditionType === WorkflowConditionType.ModeratorApproval) {
      effectiveNodeType = WorkflowNodeType.ModeratorApproval
    }
  }

  return {
    id: node.id,
    type: getNodeTypeName(effectiveNodeType),
    position: { x: node.positionX, y: node.positionY },
    data: {
      label: node.label,
      nodeType: effectiveNodeType,
      sequenceNumber: node.sequenceNumber,  // Will be assigned in loadWorkflow if missing
      description: node.description,
      loopCooldownSeconds: node.loopCooldownSeconds,
      loopTargetNodeId: node.loopTargetNodeId,
      maxLoops: node.maxLoops,
      timeBonusSeconds: node.timeBonusSeconds,
      // Challenge configs
      numpadConfig: node.numpadConfig,
      codexConfig: node.codexConfig,
      qrCodeConfig: node.qrCodeConfig,
      questionAnswerConfig: node.questionAnswerConfig,
      moderatorApprovalConfig: node.moderatorApprovalConfig,
      // Reward configs
      poiLocationConfig: node.poiLocationConfig,
      textClueConfig: node.textClueConfig,
      clueGateConfig: node.clueIds ? { clueIds: node.clueIds } : undefined,
      audioConfig: node.audioConfig,
      videoConfig: node.videoConfig,
      imageConfig: node.imageConfig,
      formattedMessageConfig: node.formattedMessageConfig,
      unlockAdventureConfig: node.unlockAdventureConfig,
      groupMembershipConfig: node.groupMembershipConfig,
      giftRewardConfig: node.giftRewardConfig,
      // Delay config
      delayConfig: node.delayConfig,
      // Route config
      routeNodeConfig: node.routeNodeConfig,
      // Timer control
      pausesTimer: node.pausesTimer,
      // Exit node message
      exitMessage: node.exitMessage,
      isRequired: node.isRequired ?? true,
      conditions: conditions.map(c => ({
        id: c.id || generateId(),
        conditionType: c.conditionType,
        config: c.config,
        branchKey: c.branchKey,
        order: c.order,
        logicGroupId: c.logicGroupId,
      })),
    },
    style: {
      borderColor: getNodeTypeColor(effectiveNodeType),
      borderWidth: 2,
    },
  }
}

// Convert backend DTO to React Flow edge
function dtoToFlowEdge(edge: FlowEdgeDto): WorkflowEdge {
  // Handle route edges with special styling
  const isRouteEdge = edge.isRouteEdge || (edge.sourceHandle === 'route-source' && edge.targetHandle === 'route-target')

  // Merge with defaults to ensure route config is always complete
  const routeConfig = isRouteEdge ? {
    ...DEFAULT_ROUTE_CONFIG,
    ...edge.routeConfig,
  } : edge.routeConfig

  // Apply route styling - use defaults if routeConfig is missing
  const routeStyle = isRouteEdge ? {
    stroke: routeConfig?.lineColor || DEFAULT_ROUTE_CONFIG.lineColor,
    strokeWidth: 3,
    strokeDasharray: (routeConfig?.lineStyle || DEFAULT_ROUTE_CONFIG.lineStyle) === 'dashed' ? '8 4' :
                    (routeConfig?.lineStyle || DEFAULT_ROUTE_CONFIG.lineStyle) === 'dotted' ? '2 4' : undefined,
    opacity: routeConfig?.lineOpacity ?? DEFAULT_ROUTE_CONFIG.lineOpacity,
  } : undefined

  // Parse waypoints from path_data_json (backend sends this as an extra field on the edge JSON)
  let waypoints: Array<{ x: number; y: number }> | undefined
  const pathDataJson = edge.pathDataJson
  if (pathDataJson) {
    try {
      const parsed = JSON.parse(pathDataJson)
      if (parsed && Array.isArray(parsed.waypoints)) {
        waypoints = parsed.waypoints.filter(
          (p: unknown): p is { x: number; y: number } =>
            typeof p === 'object' && p !== null &&
            typeof (p as { x: unknown }).x === 'number' &&
            typeof (p as { y: unknown }).y === 'number'
        )
      }
    } catch {
      // ignore malformed path_data_json
    }
  }

  return {
    id: edge.id,
    source: edge.sourceNodeId,
    target: edge.targetNodeId,
    sourceHandle: edge.sourceHandle,
    targetHandle: edge.targetHandle,
    label: edge.label,
    type: 'waypoint',
    data: {
      conditionBranchKey: edge.conditionBranchKey,
      order: edge.order,
      isRouteEdge,
      routeConfig,
      waypoints,
    },
    animated: !!edge.conditionBranchKey, // Animate conditional edges
    style: routeStyle || (edge.conditionBranchKey ? { stroke: '#ff9800' } : undefined),
    markerEnd: isRouteEdge ? undefined : undefined, // Will be set in loadWorkflow
  }
}

// Get edge style based on source node type
function getEdgeStyleForNodeType(nodeType: WorkflowNodeType) {
  const color = getNodeTypeColor(nodeType)
  let strokeDasharray: string | undefined

  switch (nodeType) {
    case WorkflowNodeType.Decision:
      strokeDasharray = '8 4' // Dashed
      break
    case WorkflowNodeType.Loop:
      strokeDasharray = '2 4' // Dotted
      break
    case WorkflowNodeType.Exit:
    case WorkflowNodeType.ExitSuccess:
    case WorkflowNodeType.ExitFailed:
    case WorkflowNodeType.ExitAbandoned:
      strokeDasharray = '8 4 2 4' // Dash-dot
      break
    default:
      strokeDasharray = undefined // Solid
  }

  return { stroke: color, strokeWidth: 2, strokeDasharray }
}

// Get React Flow node type name from enum
function getNodeTypeName(type: WorkflowNodeType): string {
  switch (type) {
    case WorkflowNodeType.Start:
      return 'startNode'
    case WorkflowNodeType.LocationAction:
      return 'locationNode'
    case WorkflowNodeType.Decision:
      return 'decisionNode'
    case WorkflowNodeType.Exit:
    case WorkflowNodeType.ExitSuccess:
    case WorkflowNodeType.ExitFailed:
    case WorkflowNodeType.ExitAbandoned:
      return 'exitNode'
    case WorkflowNodeType.Loop:
      return 'loopNode'
    case WorkflowNodeType.Delay:
      return 'delayNode'
    // Challenge node types
    case WorkflowNodeType.QuestionAnswer:
      return 'questionAnswerNode'
    case WorkflowNodeType.QRCode:
      return 'qrCodeNode'
    case WorkflowNodeType.NumericInput:
      return 'numericInputNode'
    case WorkflowNodeType.CodexInput:
      return 'codexInputNode'
    case WorkflowNodeType.ModeratorApproval:
      return 'moderatorApprovalNode'
    // Reward node types
    case WorkflowNodeType.PoiLocation:
      return 'poiLocationNode'
    case WorkflowNodeType.TextClue:
      return 'textClueNode'
    case WorkflowNodeType.AudioPlayer:
      return 'audioPlayerNode'
    case WorkflowNodeType.VideoPlayer:
      return 'videoPlayerNode'
    case WorkflowNodeType.ImageDisplay:
      return 'imageDisplayNode'
    case WorkflowNodeType.UnlockAdventure:
      return 'unlockAdventureNode'
    case WorkflowNodeType.TimeModifier:
      return 'timeModifierNode'
    case WorkflowNodeType.FormattedMessage:
      return 'formattedMessageNode'
    case WorkflowNodeType.GroupMembership:
      return 'groupMembershipNode'
    case WorkflowNodeType.GiftReward:
      return 'giftRewardNode'
    case WorkflowNodeType.Route:
      return 'routeNode'
    case WorkflowNodeType.TimerControl:
      return 'timerControlNode'
    default:
      return 'default'
  }
}

// Generate unique GUID
function generateId(): string {
  // Use crypto.randomUUID if available (secure context), otherwise fallback
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback UUID v4 generation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

// Get default config for a condition type
export function getDefaultConditionConfig(conditionType: WorkflowConditionType): Record<string, unknown> {
  switch (conditionType) {
    // Day/Night condition
    case WorkflowConditionType.WeatherDayNight:
      return {
        requiredTimeOfDay: 'day',
        useUserLocation: false,
        fixedLatitude: 0,
        fixedLongitude: 0,
      }
    // Sunset/Sunrise condition
    case WorkflowConditionType.WeatherSunsetSunrise:
      return {
        useUserLocation: false,
        fixedLatitude: 0,
        fixedLongitude: 0,
        windowMinutes: 30,
        requireSunset: true,
        requireSunrise: false,
      }
    // Temperature condition
    case WorkflowConditionType.WeatherTemperature:
      return {
        minTemperature: 32,
        maxTemperature: 100,
        useUserLocation: false,
        fixedLatitude: 0,
        fixedLongitude: 0,
      }
    // Humidity condition
    case WorkflowConditionType.WeatherHumidity:
      return {
        minHumidity: 0,
        maxHumidity: 100,
        useUserLocation: false,
        fixedLatitude: 0,
        fixedLongitude: 0,
      }
    // Wind speed condition
    case WorkflowConditionType.WeatherWindSpeed:
      return {
        minWindSpeed: 0,
        maxWindSpeed: 75,
        useUserLocation: false,
        fixedLatitude: 0,
        fixedLongitude: 0,
      }
    // Wind direction condition
    case WorkflowConditionType.WeatherWindDirection:
      return {
        directions: ['N'],
        useUserLocation: false,
        fixedLatitude: 0,
        fixedLongitude: 0,
      }
    // Cloud cover condition
    case WorkflowConditionType.WeatherCloudCover:
      return {
        minCloudCover: 0,
        maxCloudCover: 100,
        useUserLocation: false,
        fixedLatitude: 0,
        fixedLongitude: 0,
      }
    // Visibility condition
    case WorkflowConditionType.WeatherVisibility:
      return {
        minVisibility: 0,
        maxVisibility: 10,
        useUserLocation: false,
        fixedLatitude: 0,
        fixedLongitude: 0,
      }
    // Weather condition (code-based)
    case WorkflowConditionType.WeatherCondition:
      return {
        weatherCodes: [],
        useUserLocation: false,
        fixedLatitude: 0,
        fixedLongitude: 0,
      }
    // Celestial conditions
    case WorkflowConditionType.CelestialSolarNoon:
      return {
        windowMinutes: 30,
        useUserLocation: false,
        fixedLatitude: 0,
        fixedLongitude: 0,
      }
    case WorkflowConditionType.CelestialTwilight:
      return {
        twilightType: 'civil',
        useUserLocation: false,
        fixedLatitude: 0,
        fixedLongitude: 0,
      }
    case WorkflowConditionType.CelestialMoonRiseSet:
      return {
        windowMinutes: 30,
        requireMoonrise: true,
        requireMoonset: false,
        useUserLocation: false,
        fixedLatitude: 0,
        fixedLongitude: 0,
      }
    case WorkflowConditionType.CelestialMoonPhase:
      return {
        phases: ['full'],
      }
    case WorkflowConditionType.CelestialMoonAge:
      return {
        minAge: 0,
        maxAge: 29,
      }
    // Time schedule condition
    case WorkflowConditionType.TimeSchedule:
      return {
        daysOfWeek: [],
        startTime: '09:00',
        endTime: '17:00',
      }
    // Geofence condition
    case WorkflowConditionType.Geofence:
      return {
        latitude: 0,
        longitude: 0,
        radiusFeet: 100,
      }
    // Adventure completion conditions
    case WorkflowConditionType.AdventureCollectionCompletion:
      return {
        adventureIds: [],
        requireAll: true,
      }
    case WorkflowConditionType.AdventureCompleted:
      return {
        adventureId: '',
      }
    // Group membership condition
    case WorkflowConditionType.GroupMembership:
      return {
        groupId: '',
      }
    // User conditions
    case WorkflowConditionType.UserInterests:
      return {
        interestTagIds: [],
        requireAll: false,
      }
    case WorkflowConditionType.UserGenderIdentity:
      return {
        genderIdentityTagIds: [],
      }
    case WorkflowConditionType.UserRank:
      return {
        minRank: 1,
        maxRank: 100,
      }
    default:
      return {}
  }
}

// Maximum number of undo steps to keep
const MAX_UNDO_STACK_SIZE = 30

const initialState = {
  adventureId: null,
  workflowId: null,
  name: '',
  description: '',
  version: 1,
  isActive: true,
  // Flow version state
  versionNumber: null as number | null,
  versionStatus: null as FlowVersionStatus | null,
  isCompiled: false,
  // Collaboration state
  changeSequence: 0,
  // React Flow state
  nodes: [] as WorkflowNode[],
  edges: [] as WorkflowEdge[],
  selectedNodeId: null,
  selectedEdgeId: null,
  pendingEdgeSplit: null as { edgeId: string; x: number; y: number } | null,
  isDirty: false,
  isLoadingWorkflow: false,
  isLoading: false,
  isSaving: false,
  error: null,
  validationResult: null,
  // Undo/Redo state
  undoStack: [] as Array<{ nodes: WorkflowNode[]; edges: WorkflowEdge[] }>,
  redoStack: [] as Array<{ nodes: WorkflowNode[]; edges: WorkflowEdge[] }>,
  canUndo: false,
  canRedo: false,
}

export const useWorkflowStore = create<WorkflowState>()((set, get) => ({
  ...initialState,

  // State management
  setAdventureId: (id) => set({ adventureId: id }),
  setLoading: (loading) => set({ isLoading: loading }),
  setSaving: (saving) => set({ isSaving: saving }),
  setError: (error) => set({ error }),
  setDirty: (dirty) => set({ isDirty: dirty }),
  setValidationResult: (result) => set({ validationResult: result }),
  setVersionInfo: (info) => set({
    versionNumber: info.versionNumber,
    versionStatus: info.status,
    isCompiled: info.isCompiled,
  }),

  // Collaboration actions
  incrementChangeSequence: () => {
    const newSeq = get().changeSequence + 1
    set({ changeSequence: newSeq })
    return newSeq
  },
  getChangeSequence: () => get().changeSequence,

  // Validation actions
  runValidation: () => {
    const { nodes, edges } = get()
    const result = runValidation({ nodes, edges })
    set({ validationResult: result })
    return result
  },

  clearValidation: () => {
    set({ validationResult: null })
  },

  getNodeValidationState: (nodeId: string) => {
    const { validationResult } = get()
    return validationResult?.nodeStates.get(nodeId)
  },

  // Undo/Redo actions
  pushToUndoStack: () => {
    const { nodes, edges, undoStack } = get()
    // Deep clone the current state
    const snapshot = {
      nodes: JSON.parse(JSON.stringify(nodes)) as WorkflowNode[],
      edges: JSON.parse(JSON.stringify(edges)) as WorkflowEdge[],
    }
    // Add to undo stack, limit to MAX_UNDO_STACK_SIZE
    const newUndoStack = [...undoStack, snapshot].slice(-MAX_UNDO_STACK_SIZE)
    set({
      undoStack: newUndoStack,
      redoStack: [], // Clear redo stack when new action is performed
      canUndo: newUndoStack.length > 0,
      canRedo: false,
    })
  },

  undo: () => {
    const { nodes, edges, undoStack, redoStack } = get()
    if (undoStack.length === 0) return

    // Save current state to redo stack
    const currentSnapshot = {
      nodes: JSON.parse(JSON.stringify(nodes)) as WorkflowNode[],
      edges: JSON.parse(JSON.stringify(edges)) as WorkflowEdge[],
    }
    const newRedoStack = [...redoStack, currentSnapshot].slice(-MAX_UNDO_STACK_SIZE)

    // Pop from undo stack and restore
    const newUndoStack = [...undoStack]
    const previousState = newUndoStack.pop()!

    set({
      nodes: previousState.nodes,
      edges: previousState.edges,
      undoStack: newUndoStack,
      redoStack: newRedoStack,
      canUndo: newUndoStack.length > 0,
      canRedo: true,
      isDirty: true,
    })
  },

  redo: () => {
    const { nodes, edges, undoStack, redoStack } = get()
    if (redoStack.length === 0) return

    // Save current state to undo stack
    const currentSnapshot = {
      nodes: JSON.parse(JSON.stringify(nodes)) as WorkflowNode[],
      edges: JSON.parse(JSON.stringify(edges)) as WorkflowEdge[],
    }
    const newUndoStack = [...undoStack, currentSnapshot].slice(-MAX_UNDO_STACK_SIZE)

    // Pop from redo stack and restore
    const newRedoStack = [...redoStack]
    const nextState = newRedoStack.pop()!

    set({
      nodes: nextState.nodes,
      edges: nextState.edges,
      undoStack: newUndoStack,
      redoStack: newRedoStack,
      canUndo: true,
      canRedo: newRedoStack.length > 0,
      isDirty: true,
    })
  },

  // Load/Save
  loadWorkflow: (workflow) => {
    // Set flag to prevent onNodesChange/onEdgesChange from setting isDirty
    set({ isLoadingWorkflow: true })

    let nodes = workflow.flowData.nodes.map(dtoToFlowNode)
    const edges = workflow.flowData.edges.map(dtoToFlowEdge)

    // Auto-create a Start node if one doesn't exist
    const hasStartNode = nodes.some(n => n.data.nodeType === WorkflowNodeType.Start)
    if (!hasStartNode) {
      const startNode: WorkflowNode = {
        id: generateId(),
        type: 'startNode',
        position: { x: 250, y: 100 },
        data: {
          label: 'Start',
          nodeType: WorkflowNodeType.Start,
          sequenceNumber: 1,
          isRequired: true,
          conditions: [],
        },
        style: {
          borderColor: getNodeTypeColor(WorkflowNodeType.Start),
          borderWidth: 2,
        },
      }
      nodes = [startNode, ...nodes]
    }

    // Assign sequence numbers to nodes that don't have them (migration for existing workflows)
    const maxSeqByType = new Map<WorkflowNodeType, number>()
    // First pass: find max existing sequence numbers per type
    nodes.forEach(node => {
      if (node.data.sequenceNumber) {
        const currentMax = maxSeqByType.get(node.data.nodeType) ?? 0
        maxSeqByType.set(node.data.nodeType, Math.max(currentMax, node.data.sequenceNumber))
      }
    })
    // Second pass: assign sequence numbers to nodes that don't have them
    nodes.forEach(node => {
      if (!node.data.sequenceNumber) {
        const currentMax = maxSeqByType.get(node.data.nodeType) ?? 0
        node.data.sequenceNumber = currentMax + 1
        maxSeqByType.set(node.data.nodeType, currentMax + 1)
      }
    })

    // Apply edge styling based on source node type (but preserve route edge styling)
    const styledEdges = edges.map((edge) => {
      // Route edges keep their own styling
      if (edge.data?.isRouteEdge) {
        return edge
      }

      const sourceNode = nodes.find((n) => n.id === edge.source)
      if (sourceNode) {
        const style = getEdgeStyleForNodeType(sourceNode.data.nodeType)
        return { ...edge, style, markerEnd: 'url(#arrow)' }
      }
      return edge
    })

    // Ensure status is converted to number (API may return string)
    const status = typeof workflow.status === 'string'
      ? parseInt(workflow.status, 10)
      : workflow.status

    set({
      versionNumber: workflow.versionNumber,
      versionStatus: status as FlowVersionStatus,
      isCompiled: workflow.isCompiled,
      description: workflow.description || '',
      nodes,
      edges: styledEdges,
      isDirty: false, // Loading from server is always clean state
      error: null,
      // Clear undo/redo stacks when loading a workflow
      undoStack: [],
      redoStack: [],
      canUndo: false,
      canRedo: false,
    })

    // Use setTimeout to clear flag after React Flow processes the changes
    setTimeout(() => {
      set({ isLoadingWorkflow: false })
    }, 100)
  },

  // Initialize an empty workflow with a default Start node
  initializeEmptyWorkflow: () => {
    const startNode: WorkflowNode = {
      id: generateId(),
      type: 'startNode',
      position: { x: 250, y: 100 },
      data: {
        label: 'Start',
        nodeType: WorkflowNodeType.Start,
        sequenceNumber: 1,
        isRequired: true,
        conditions: [],
      },
      style: {
        borderColor: getNodeTypeColor(WorkflowNodeType.Start),
        borderWidth: 2,
      },
    }

    set({
      ...initialState,
      nodes: [startNode],
      isDirty: true, // Mark dirty since it's a new unsaved workflow
      // Clear undo/redo stacks for new workflow
      undoStack: [],
      redoStack: [],
      canUndo: false,
      canRedo: false,
    })
  },

  resetWorkflow: () => set(initialState),

  // Import from JSON (for testing)
  loadFromJson: (data) => {
    // Convert imported nodes to React Flow format
    let nodes: WorkflowNode[] = data.nodes.map((node) => ({
      id: node.id || generateId(),
      type: getNodeTypeName(node.nodeType),
      position: { x: node.positionX, y: node.positionY },
      data: {
        label: node.label,
        nodeType: node.nodeType,
        description: node.description,
        rewardType: node.rewardType,
        locationItemId: node.locationItemId,
        loopCooldownSeconds: node.loopCooldownSeconds,
        loopTargetNodeId: node.loopTargetNodeId,
        maxLoops: node.maxLoops,
        timeBonusSeconds: node.timeBonusSeconds,
        numpadConfig: node.numpadConfig,
        codexConfig: node.codexConfig,
        qrCodeConfig: node.qrCodeConfig,
        questionAnswerConfig: node.questionAnswerConfig,
        poiLocationConfig: node.poiLocationConfig,
        textClueConfig: node.textClueConfig,
        clueGateConfig: node.clueGateConfig,
        audioConfig: node.audioConfig,
        videoConfig: node.videoConfig,
        imageConfig: node.imageConfig,
        formattedMessageConfig: node.formattedMessageConfig,
        unlockAdventureConfig: node.unlockAdventureConfig,
        groupMembershipConfig: node.groupMembershipConfig,
        giftRewardConfig: node.giftRewardConfig,
        exitMessage: node.exitMessage,
        isRequired: node.isRequired,
        config: node.config,
        conditions: (node.conditions || []).map((c) => ({
          id: c.id || generateId(),
          conditionType: c.conditionType,
          config: c.config,
          branchKey: c.branchKey,
          order: c.order,
          logicGroupId: c.logicGroupId,
        })),
      },
      style: {
        borderColor: getNodeTypeColor(node.nodeType),
        borderWidth: 2,
      },
    }))

    // Convert imported edges to React Flow format
    const edges: WorkflowEdge[] = data.edges.map((edge) => {
      const sourceNode = nodes.find((n) => n.id === edge.sourceNodeId)
      const style = sourceNode ? getEdgeStyleForNodeType(sourceNode.data.nodeType) : undefined

      // Handle route edges
      const isRouteEdge = edge.isRouteEdge || (edge.sourceHandle === 'route-source' && edge.targetHandle === 'route-target')

      // Merge with defaults to ensure route config is always complete
      const routeConfig = isRouteEdge ? {
        ...DEFAULT_ROUTE_CONFIG,
        ...edge.routeConfig,
      } : edge.routeConfig

      // Apply route styling - use defaults if routeConfig is missing
      const routeStyle = isRouteEdge ? {
        stroke: routeConfig?.lineColor || DEFAULT_ROUTE_CONFIG.lineColor,
        strokeWidth: 3,
        strokeDasharray: (routeConfig?.lineStyle || DEFAULT_ROUTE_CONFIG.lineStyle) === 'dashed' ? '8 4' :
                        (routeConfig?.lineStyle || DEFAULT_ROUTE_CONFIG.lineStyle) === 'dotted' ? '2 4' : undefined,
        opacity: routeConfig?.lineOpacity ?? DEFAULT_ROUTE_CONFIG.lineOpacity,
      } : undefined

      return {
        id: edge.id || generateId(),
        source: edge.sourceNodeId,
        target: edge.targetNodeId,
        sourceHandle: edge.sourceHandle,
        targetHandle: edge.targetHandle,
        label: edge.label,
        data: {
          conditionBranchKey: edge.conditionBranchKey,
          order: edge.order,
          isRouteEdge,
          routeConfig,
        },
        animated: !!edge.conditionBranchKey,
        style: routeStyle || (edge.conditionBranchKey ? { stroke: '#ff9800' } : style),
        markerEnd: isRouteEdge ? undefined : 'url(#arrow)',
      }
    })

    // Auto-create a Start node if one doesn't exist
    const hasStartNode = nodes.some(n => n.data.nodeType === WorkflowNodeType.Start)
    if (!hasStartNode) {
      const startNode: WorkflowNode = {
        id: generateId(),
        type: 'startNode',
        position: { x: 250, y: 100 },
        data: {
          label: 'Start',
          nodeType: WorkflowNodeType.Start,
          isRequired: true,
          conditions: [],
        },
        style: {
          borderColor: getNodeTypeColor(WorkflowNodeType.Start),
          borderWidth: 2,
        },
      }
      nodes = [startNode, ...nodes]
    }

    set({
      name: data.name || '',
      description: data.description || '',
      nodes,
      edges,
      isDirty: true, // Mark as dirty since it's not saved
      error: null,
      selectedNodeId: null,
      selectedEdgeId: null,
      // Clear undo/redo stacks when importing
      undoStack: [],
      redoStack: [],
      canUndo: false,
      canRedo: false,
    })
  },

  // React Flow callbacks
  onNodesChange: (changes) => {
    const { isLoadingWorkflow } = get()
    set({
      nodes: applyNodeChanges(changes, get().nodes) as WorkflowNode[],
      // Don't mark dirty during workflow load
      ...(isLoadingWorkflow ? {} : { isDirty: true, validationResult: null }),
    })
  },

  onEdgesChange: (changes) => {
    const { isLoadingWorkflow } = get()
    set({
      edges: applyEdgeChanges(changes, get().edges),
      // Don't mark dirty during workflow load
      ...(isLoadingWorkflow ? {} : { isDirty: true, validationResult: null }),
    })
  },

  onConnect: (connection: Connection) => {
    // Prevent self-connections (node connecting to itself)
    if (connection.source === connection.target) {
      return
    }

    // Detect if this is a route edge (route-source -> route-target)
    const isRouteEdge = connection.sourceHandle === 'route-source' &&
                        connection.targetHandle === 'route-target'

    // Get source node to determine edge styling
    const sourceNode = get().nodes.find((n) => n.id === connection.source)
    const style = sourceNode ? getEdgeStyleForNodeType(sourceNode.data.nodeType) : undefined

    // Route edges have special styling
    const routeStyle = isRouteEdge ? {
      stroke: DEFAULT_ROUTE_CONFIG.lineColor,
      strokeWidth: 3,
      strokeDasharray: '8 4',
      opacity: DEFAULT_ROUTE_CONFIG.lineOpacity,
    } : undefined

    const newEdge: WorkflowEdge = {
      id: generateId(),
      source: connection.source!,
      target: connection.target!,
      sourceHandle: connection.sourceHandle || undefined,
      targetHandle: connection.targetHandle || undefined,
      type: 'waypoint',
      data: isRouteEdge ? {
        isRouteEdge: true,
        routeConfig: { ...DEFAULT_ROUTE_CONFIG },
      } : undefined,
      style: routeStyle || style,
      markerEnd: isRouteEdge ? undefined : 'url(#arrow)',
    }
    set({
      edges: addEdge(newEdge, get().edges),
      isDirty: true,
    })
  },

  // Selection
  selectNode: (nodeId) => set({ selectedNodeId: nodeId, selectedEdgeId: null, pendingEdgeSplit: null }),
  selectEdge: (edgeId) => {
    const current = get().pendingEdgeSplit
    set({
      selectedEdgeId: edgeId,
      selectedNodeId: null,
      // Clear any stale split-affordance from a previously selected edge.
      pendingEdgeSplit: current && current.edgeId === edgeId ? current : null,
    })
  },
  setPendingEdgeSplit: (value) => set({ pendingEdgeSplit: value }),

  // Node operations
  addNode: (type, position, label, conditionType, rewardType) => {
    const id = generateId()

    // Generate unique numbered label if no label provided
    let defaultLabel = label
    if (!defaultLabel) {
      const baseLabel = `New ${getNodeTypeLabel(type)} Node`
      const existingNodes = get().nodes

      // Find all existing numbers used for this node type
      const pattern = new RegExp(`^${baseLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*(\\d+)?$`)
      const usedNumbers = new Set<number>()

      existingNodes.forEach(node => {
        const match = node.data.label.match(pattern)
        if (match) {
          // If no number, treat as 1; otherwise parse the number
          usedNumbers.add(match[1] ? parseInt(match[1], 10) : 1)
        }
      })

      // Find the next available number
      let nextNum = 1
      while (usedNumbers.has(nextNum)) {
        nextNum++
      }

      defaultLabel = `${baseLabel} ${nextNum}`
    }

    // Challenge node types don't use conditions array - they use their specific config
    // Only Decision nodes (logical conditions) use the conditions array
    const isChallengeNode = [
      WorkflowNodeType.QuestionAnswer,
      WorkflowNodeType.QRCode,
      WorkflowNodeType.NumericInput,
      WorkflowNodeType.CodexInput,
      WorkflowNodeType.ModeratorApproval,
    ].includes(type)

    // Create default condition if conditionType is specified (for Decision nodes only)
    const conditions: WorkflowNodeData['conditions'] = (!isChallengeNode && conditionType !== undefined)
      ? [{
          id: generateId(),
          conditionType,
          config: getDefaultConditionConfig(conditionType),
          order: 0,
        }]
      : []

    // Initialize numpadConfig for Number Pad nodes (now based on nodeType)
    const numpadConfig: NumpadConfig | undefined = type === WorkflowNodeType.NumericInput
      ? {
          digitCount: 4,
          inputStyle: NumpadInputStyle.TenKey,
          acceptedNumbers: [],
        }
      : undefined

    // Initialize qrCodeConfig for QR Scanner nodes (now based on nodeType)
    const qrCodeConfig: QrCodeConfig | undefined = type === WorkflowNodeType.QRCode
      ? {
          sharedCodes: [],
        }
      : undefined

    // Initialize questionAnswerConfig for Question/Answer nodes (now based on nodeType)
    const questionAnswerConfig: QuestionAnswerConfig | undefined = type === WorkflowNodeType.QuestionAnswer
      ? {
          questionText: '',
          answerType: QuestionAnswerType.FreeText,
          allowSkip: false,
        }
      : undefined

    // Initialize codexConfig for Codex nodes (A-Z letter roller)
    const codexConfig: CodexConfig | undefined = type === WorkflowNodeType.CodexInput
      ? {
          letterCount: 4,
          acceptedCodes: [],
        }
      : undefined

    // Initialize moderatorApprovalConfig for Moderator Approval nodes (now based on nodeType)
    const moderatorApprovalConfig: ModeratorApprovalConfig | undefined = type === WorkflowNodeType.ModeratorApproval
      ? {}
      : undefined

    // Initialize delayConfig for Delay nodes
    const delayConfig: DelayConfig | undefined = type === WorkflowNodeType.Delay
      ? {
          duration: 5,
          unit: DelayTimeUnit.Minutes,
        }
      : undefined

    // Initialize pausesTimer for TimerControl nodes
    const pausesTimer: boolean | undefined = type === WorkflowNodeType.TimerControl
      ? true
      : undefined

    // Initialize reward-specific configs based on nodeType
    const poiLocationConfig: PoiLocationConfig | undefined = type === WorkflowNodeType.PoiLocation
      ? {
          latitude: 0,
          longitude: 0,
          markerEmoji: '📍',
        }
      : undefined

    const textClueConfig: TextClueConfig | undefined = type === WorkflowNodeType.TextClue
      ? {
          message: '',
        }
      : undefined

    const clueGateConfig: ClueGateConfig | undefined = type === WorkflowNodeType.TextClue
      ? {
          clueIds: [],
        }
      : undefined

    const videoConfig: VideoConfig | undefined = type === WorkflowNodeType.VideoPlayer
      ? {
          videoUrl: '',
          hostName: '',
        }
      : undefined

    const formattedMessageConfig: FormattedMessageConfig | undefined = type === WorkflowNodeType.FormattedMessage
      ? {
          htmlContent: '',
        }
      : undefined

    const unlockAdventureConfig: UnlockAdventureConfig | undefined = type === WorkflowNodeType.UnlockAdventure
      ? {
          adventureId: '',
        }
      : undefined

    // Initialize routeNodeConfig for Route nodes
    const routeNodeConfig: RouteNodeConfig | undefined = type === WorkflowNodeType.Route
      ? { ...DEFAULT_ROUTE_NODE_CONFIG }
      : undefined

    // Default loopCooldownSeconds for Loop nodes
    const loopCooldownSeconds: number | undefined = type === WorkflowNodeType.Loop ? 30 : undefined

    // Calculate next sequence number for this node type
    const existingNodesOfType = get().nodes.filter(n => n.data.nodeType === type)
    const maxSequence = existingNodesOfType.reduce(
      (max, n) => Math.max(max, n.data.sequenceNumber ?? 0),
      0
    )
    const sequenceNumber = maxSequence + 1

    const newNode: WorkflowNode = {
      id,
      type: getNodeTypeName(type),
      position,
      data: {
        label: defaultLabel,
        nodeType: type,
        sequenceNumber,
        isRequired: true,
        conditions,
        rewardType,
        // Loop config
        loopCooldownSeconds,
        // Challenge configs
        numpadConfig,
        codexConfig,
        qrCodeConfig,
        questionAnswerConfig,
        moderatorApprovalConfig,
        delayConfig,
        // Reward configs
        poiLocationConfig,
        textClueConfig,
        clueGateConfig,
        videoConfig,
        formattedMessageConfig,
        unlockAdventureConfig,
        // Route config
        routeNodeConfig,
        // Timer control
        pausesTimer,
      },
      style: {
        borderColor: getNodeTypeColor(type),
        borderWidth: 2,
      },
    }

    set({
      nodes: [...get().nodes, newNode],
      isDirty: true,
    })

    return id
  },

  updateNode: (nodeId, data) => {
    // Push current state to undo stack before updating
    const { nodes, edges, undoStack } = get()
    const snapshot = {
      nodes: JSON.parse(JSON.stringify(nodes)) as WorkflowNode[],
      edges: JSON.parse(JSON.stringify(edges)) as WorkflowEdge[],
    }
    const newUndoStack = [...undoStack, snapshot].slice(-MAX_UNDO_STACK_SIZE)

    set({
      nodes: nodes.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, ...data } }
          : node
      ),
      isDirty: true,
      undoStack: newUndoStack,
      redoStack: [],
      canUndo: newUndoStack.length > 0,
      canRedo: false,
    })
  },

  deleteNode: (nodeId) => {
    // Push current state to undo stack before deleting
    const { nodes, edges, undoStack, selectedNodeId } = get()
    const snapshot = {
      nodes: JSON.parse(JSON.stringify(nodes)) as WorkflowNode[],
      edges: JSON.parse(JSON.stringify(edges)) as WorkflowEdge[],
    }
    const newUndoStack = [...undoStack, snapshot].slice(-MAX_UNDO_STACK_SIZE)

    set({
      nodes: nodes.filter((n) => n.id !== nodeId),
      edges: edges.filter((e) => e.source !== nodeId && e.target !== nodeId),
      selectedNodeId: selectedNodeId === nodeId ? null : selectedNodeId,
      isDirty: true,
      undoStack: newUndoStack,
      redoStack: [],
      canUndo: newUndoStack.length > 0,
      canRedo: false,
    })
  },

  updateNodePosition: (nodeId, position) => {
    set({
      nodes: get().nodes.map((node) =>
        node.id === nodeId ? { ...node, position } : node
      ),
      isDirty: true,
    })
  },

  // Edge operations
  addEdge: (sourceId, targetId, label, branchKey) => {
    const id = generateId()
    const newEdge: WorkflowEdge = {
      id,
      source: sourceId,
      target: targetId,
      label,
      type: 'waypoint',
      data: {
        conditionBranchKey: branchKey,
      },
      animated: !!branchKey,
      style: branchKey ? { stroke: '#ff9800' } : undefined,
    }

    set({
      edges: [...get().edges, newEdge],
      isDirty: true,
    })

    return id
  },

  updateEdge: (edgeId, data) => {
    // Push current state to undo stack before updating
    const { nodes, edges, undoStack } = get()
    const snapshot = {
      nodes: JSON.parse(JSON.stringify(nodes)) as WorkflowNode[],
      edges: JSON.parse(JSON.stringify(edges)) as WorkflowEdge[],
    }
    const newUndoStack = [...undoStack, snapshot].slice(-MAX_UNDO_STACK_SIZE)

    set({
      edges: edges.map((edge) =>
        edge.id === edgeId
          ? {
              ...edge,
              label: data.label ?? edge.label,
              data: {
                ...edge.data,
                conditionBranchKey: data.conditionBranchKey ?? edge.data?.conditionBranchKey,
              },
            }
          : edge
      ),
      isDirty: true,
      undoStack: newUndoStack,
      redoStack: [],
      canUndo: newUndoStack.length > 0,
      canRedo: false,
    })
  },

  deleteEdge: (edgeId) => {
    set({
      edges: get().edges.filter((e) => e.id !== edgeId),
      selectedEdgeId: get().selectedEdgeId === edgeId ? null : get().selectedEdgeId,
      isDirty: true,
    })
  },

  updateRouteConfig: (edgeId, config) => {
    // Push current state to undo stack before updating
    const { nodes, edges, undoStack } = get()
    const snapshot = {
      nodes: JSON.parse(JSON.stringify(nodes)) as WorkflowNode[],
      edges: JSON.parse(JSON.stringify(edges)) as WorkflowEdge[],
    }
    const newUndoStack = [...undoStack, snapshot].slice(-MAX_UNDO_STACK_SIZE)

    set({
      edges: edges.map((edge) => {
        if (edge.id !== edgeId || !edge.data?.isRouteEdge) return edge

        const existingRouteConfig = (edge.data.routeConfig as RouteConfig | undefined) ?? DEFAULT_ROUTE_CONFIG
        const newRouteConfig: RouteConfig = {
          ...existingRouteConfig,
          ...config,
        }

        // Update edge style based on new route config
        const newStyle = {
          stroke: newRouteConfig.lineColor,
          strokeWidth: 3,
          strokeDasharray: newRouteConfig.lineStyle === 'dashed' ? '8 4' :
                          newRouteConfig.lineStyle === 'dotted' ? '2 4' : undefined,
          opacity: newRouteConfig.lineOpacity,
        }

        return {
          ...edge,
          data: {
            ...edge.data,
            routeConfig: newRouteConfig,
          },
          style: newStyle,
        }
      }),
      isDirty: true,
      undoStack: newUndoStack,
      redoStack: [],
      canUndo: newUndoStack.length > 0,
      canRedo: false,
    })
  },

  updateEdgeWaypoints: (edgeId, waypoints, options) => {
    const pushUndo = options?.pushUndo ?? true
    const markDirty = options?.markDirty ?? true
    const { nodes, edges, undoStack } = get()

    const nextEdges = edges.map((edge) =>
      edge.id === edgeId
        ? {
            ...edge,
            data: {
              ...edge.data,
              waypoints: waypoints.length > 0 ? waypoints : undefined,
            },
          }
        : edge
    )

    if (pushUndo) {
      const snapshot = {
        nodes: JSON.parse(JSON.stringify(nodes)) as WorkflowNode[],
        edges: JSON.parse(JSON.stringify(edges)) as WorkflowEdge[],
      }
      const newUndoStack = [...undoStack, snapshot].slice(-MAX_UNDO_STACK_SIZE)
      set({
        edges: nextEdges,
        isDirty: markDirty ? true : get().isDirty,
        undoStack: newUndoStack,
        redoStack: [],
        canUndo: newUndoStack.length > 0,
        canRedo: false,
      })
    } else {
      set({
        edges: nextEdges,
        isDirty: markDirty ? true : get().isDirty,
      })
    }
  },

  // Update node ID (for syncing temp IDs with server-generated IDs)
  updateNodeId: (oldId, newId) => {
    set({
      nodes: get().nodes.map((node) =>
        node.id === oldId ? { ...node, id: newId } : node
      ),
      edges: get().edges.map((edge) => ({
        ...edge,
        source: edge.source === oldId ? newId : edge.source,
        target: edge.target === oldId ? newId : edge.target,
      })),
      selectedNodeId: get().selectedNodeId === oldId ? newId : get().selectedNodeId,
    })
  },

  // Update edge ID (for syncing temp IDs with server-generated IDs)
  updateEdgeId: (oldId, newId) => {
    set({
      edges: get().edges.map((edge) =>
        edge.id === oldId ? { ...edge, id: newId } : edge
      ),
      selectedEdgeId: get().selectedEdgeId === oldId ? newId : get().selectedEdgeId,
    })
  },

  // Add a node from existing data (for rollback operations)
  addNodeFromData: (node) => {
    set({
      nodes: [...get().nodes, node],
      isDirty: true,
    })
  },

  // Add an edge from existing data (for rollback operations)
  addEdgeFromData: (edge) => {
    set({
      edges: [...get().edges, edge],
      isDirty: true,
    })
  },

  // Load workflow from remote replace (when another user loads a version or imports)
  // This replaces the entire workflow without marking dirty (since it came from another user)
  loadFromRemoteReplace: (nodeData, edgeData, incomingChangeSequence) => {
    // Convert the incoming data to WorkflowNode format
    const nodes: WorkflowNode[] = nodeData.map(n => ({
      id: n.id,
      type: n.type,
      position: n.position,
      data: n.data as WorkflowNodeData,
    }))

    // Convert the incoming data to WorkflowEdge format with proper styling
    const edges: WorkflowEdge[] = edgeData.map(e => {
      const sourceNode = nodes.find(n => n.id === e.source)
      const isRouteEdge = e.data?.isRouteEdge === true

      // Determine edge style based on source node type
      let style: WorkflowEdge['style'] = { strokeWidth: 2 }
      if (isRouteEdge) {
        // Preserve route edge styling
        style = {
          strokeWidth: 3,
          stroke: '#9c27b0',
          strokeDasharray: '5,5',
        }
      } else if (sourceNode?.data?.nodeType === WorkflowNodeType.Decision) {
        style = { strokeWidth: 2, stroke: '#ff9800' }
      }

      return {
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle,
        targetHandle: e.targetHandle,
        type: 'waypoint',
        markerEnd: { type: MarkerType.ArrowClosed },
        data: e.data as WorkflowEdgeData | undefined,
        style,
      }
    })

    set((state) => ({
      nodes,
      edges,
      // Update change sequence if provided (and it's higher than current)
      changeSequence: incomingChangeSequence !== undefined && incomingChangeSequence > state.changeSequence
        ? incomingChangeSequence
        : state.changeSequence,
      // Don't mark dirty - this came from another user
      // Don't clear undo stack - preserve local history
      selectedNodeId: null,
      selectedEdgeId: null,
    }))
  },

  // Get serializable state for saving to API (SaveFlowRequest format)
  getSerializableState: () => {
    const { description, nodes, edges } = get()

    return {
      flowData: {
        nodes: nodes.map((node, index): FlowNodeDto => ({
          id: node.id,
          nodeType: node.data.nodeType,
          sequenceNumber: node.data.sequenceNumber,
          label: node.data.label,
          description: node.data.description,
          positionX: node.position.x,
          positionY: node.position.y,
          // Default to 30 for Loop nodes if not explicitly set
          loopCooldownSeconds: node.data.loopCooldownSeconds ?? (node.data.nodeType === WorkflowNodeType.Loop ? 30 : undefined),
          loopTargetNodeId: node.data.loopTargetNodeId,
          maxLoops: node.data.maxLoops,
          timeBonusSeconds: node.data.timeBonusSeconds,
          // Challenge configs
          numpadConfig: node.data.numpadConfig,
          codexConfig: node.data.codexConfig,
          qrCodeConfig: node.data.qrCodeConfig,
          questionAnswerConfig: node.data.questionAnswerConfig,
          moderatorApprovalConfig: node.data.moderatorApprovalConfig,
          // Reward configs
          poiLocationConfig: node.data.poiLocationConfig,
          textClueConfig: node.data.textClueConfig,
          clueIds: node.data.clueGateConfig?.clueIds,
          audioConfig: node.data.audioConfig,
          videoConfig: node.data.videoConfig,
          imageConfig: node.data.imageConfig,
          formattedMessageConfig: node.data.formattedMessageConfig,
          unlockAdventureConfig: node.data.unlockAdventureConfig,
          groupMembershipConfig: node.data.groupMembershipConfig,
          giftRewardConfig: node.data.giftRewardConfig,
          // Delay config
          delayConfig: node.data.delayConfig,
          // Route config
          routeNodeConfig: node.data.routeNodeConfig,
          // Timer control
          pausesTimer: node.data.pausesTimer,
          // Exit node message
          exitMessage: node.data.exitMessage as string | undefined,
          order: index,
          isRequired: node.data.isRequired,
          conditions: node.data.conditions.map((c, ci) => ({
            id: c.id,
            conditionType: c.conditionType,
            config: c.config,
            branchKey: c.branchKey,
            order: ci,
            logicGroupId: c.logicGroupId,
          })),
        })),
        edges: edges.map((edge, index): FlowEdgeDto => {
          const waypoints = edge.data?.waypoints as Array<unknown> | undefined
          const pathDataJson = waypoints && waypoints.length > 0
            ? JSON.stringify({ version: 1, waypoints })
            : undefined
          return {
            id: edge.id,
            sourceNodeId: edge.source,
            targetNodeId: edge.target,
            sourceHandle: edge.sourceHandle as string | undefined,
            targetHandle: edge.targetHandle as string | undefined,
            label: edge.label as string | undefined,
            conditionBranchKey: edge.data?.conditionBranchKey as string | undefined,
            isRouteEdge: edge.data?.isRouteEdge as boolean | undefined,
            routeConfig: edge.data?.routeConfig as RouteConfig | undefined,
            pathDataJson,
            order: index,
          }
        }),
      },
      description,
    }
  },
}))
